import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Globe2, Sparkles, Briefcase, FileCheck, MessageSquare, Search, Wand2, BadgeCheck, ArrowUpRight, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const HERO_IMG = "https://images.unsplash.com/photo-1733195296321-b99d129b09cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxnbG9iYWwlMjB3b3JsZCUyMG5ldHdvcmslMjBtYXB8ZW58MHx8fHwxNzc3MTY3MjY2fDA&ixlib=rb-4.1.0&q=85";
const COLLAB_IMG = "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=900&q=85";
const REMOTE_IMG = "https://images.pexels.com/photos/20955061/pexels-photo-20955061.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TEAM_IMG = "https://images.unsplash.com/photo-1758518729912-bf3a84c400e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBvZmZpY2V8ZW58MHx8fHwxNzc3MTY3MjQ0fDA&ixlib=rb-4.1.0&q=85";
const AVATARS = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=facearea&facepad=2&w=120&q=85",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=facearea&facepad=2&w=120&q=85",
  "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=facearea&facepad=2&w=120&q=85",
  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=facearea&facepad=2&w=120&q=85",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=facearea&facepad=2&w=120&q=85",
];

export default function Landing() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({ candidates: 0, employers: 0, jobs: 0 });

  useEffect(() => {
    axios
      .get(`${API}/api/stats/public`)
      .then((res) => setStats(res.data))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 mec-grid-bg opacity-50" />
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white/80" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-blue-100/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-emerald-50/60 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-up">
              <Badge variant="outline" className="mb-6 border-slate-300 bg-white/90 text-slate-700 font-mono text-[10px] tracking-widest uppercase backdrop-blur" data-testid="hero-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2 animate-pulse" />
                {t("landing.hero_badge")}
              </Badge>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                {t("landing.hero_title_1")}<br />
                <span className="mec-gradient-text">{t("landing.hero_title_2")}</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                {t("landing.hero_subtitle")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register">
                  <Button size="lg" className="bg-[#004EEB] hover:bg-[#003BCC] text-white h-12 px-6" data-testid="hero-cta-register">
                    {t("landing.hero_cta_register")} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/jobs">
                  <Button size="lg" variant="outline" className="h-12 px-6 border-slate-300 bg-white/80 backdrop-blur" data-testid="hero-cta-jobs">
                    {t("landing.hero_cta_jobs")}
                  </Button>
                </Link>
              </div>

              <div className="mt-8 flex items-center gap-3">
                <div className="flex -space-x-2">
                  {AVATARS.map((src, i) => (
                    <img key={i} src={src} alt="" className="w-9 h-9 rounded-full border-2 border-white object-cover" />
                  ))}
                </div>
                <div className="text-xs text-slate-600 leading-tight">
                  <div className="font-semibold text-slate-900">{t("landing.hero_avatars_title")}</div>
                  <div>{t("landing.hero_avatars_sub")}</div>
                </div>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
                <div className="border-l border-slate-300 pl-4" data-testid="stat-candidates">
                  <div className="font-display text-2xl font-bold text-slate-900 tabular-nums">{stats.candidates.toLocaleString()}</div>
                  <div className="text-xs text-slate-500 mt-1">{t("landing.stat_candidates")}</div>
                </div>
                <div className="border-l border-slate-300 pl-4" data-testid="stat-employers">
                  <div className="font-display text-2xl font-bold text-slate-900 tabular-nums">{stats.employers.toLocaleString()}</div>
                  <div className="text-xs text-slate-500 mt-1">{t("landing.stat_employers")}</div>
                </div>
                <div className="border-l border-slate-300 pl-4" data-testid="stat-countries-block">
                  <div className="font-display text-2xl font-bold text-slate-900">70+</div>
                  <div className="text-xs text-slate-500 mt-1">{t("landing.stat_countries")}</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 fade-up fade-up-2 relative">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl shadow-slate-900/5">
                <img src={COLLAB_IMG} alt="Collaboration" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full border border-slate-200 flex items-center gap-1.5 text-xs font-semibold text-slate-900">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  {t("landing.live_badge")}
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="font-display text-lg font-semibold leading-tight">{t("landing.collab_title")}</div>
                  <div className="text-xs text-white/80 mt-0.5">{t("landing.collab_sub")}</div>
                </div>
              </div>

              <div className="absolute -left-4 sm:-left-8 top-8 mec-card p-3 max-w-[220px] shadow-lg shadow-blue-100 hidden sm:block">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-[#004EEB]" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-xs truncate">{t("landing.ai_card_title")}</div>
                    <div className="text-[10px] text-slate-500">{t("landing.ai_card_sub")}</div>
                  </div>
                </div>
              </div>

              <div className="absolute -right-2 sm:-right-6 bottom-20 mec-card p-3 shadow-lg shadow-emerald-100 hidden sm:block">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Globe2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-display text-base font-bold text-slate-900 leading-none">70+</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{t("landing.stat_countries")}</div>
                  </div>
                </div>
              </div>

              <div className="absolute -left-2 -bottom-4 mec-card p-3 shadow-lg shadow-slate-100 hidden sm:flex items-center gap-2">
                <BadgeCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <div className="text-[11px] font-semibold text-slate-900">{t("landing.verified_card")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-slate-200 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="mec-overline mb-3">{t("landing.how_overline")}</div>
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 max-w-2xl">
                {t("landing.how_title")}
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="mec-card p-8 mec-card-hover" data-testid="role-card-candidate">
              <div className="flex items-center justify-between mb-6">
                <Badge className="bg-blue-50 text-[#004EEB] hover:bg-blue-50 font-mono text-[10px] tracking-widest uppercase">{t("landing.candidates_label")}</Badge>
                <Users className="w-6 h-6 text-slate-400" />
              </div>
              <h3 className="font-display text-2xl font-semibold text-slate-900 mb-2">{t("landing.candidates_title")}</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">{t("landing.candidates_desc")}</p>
              <ul className="space-y-3 mb-6">
                {[t("landing.candidates_b1"), t("landing.candidates_b2"), t("landing.candidates_b3"), t("landing.candidates_b4")].map((txt, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <FileCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    {txt}
                  </li>
                ))}
              </ul>
              <Link to="/register?role=candidate">
                <Button variant="outline" className="w-full border-slate-300 group" data-testid="cta-candidate">
                  {t("landing.candidates_cta")}
                  <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Button>
              </Link>
            </div>

            <div className="mec-card p-8 mec-card-hover bg-slate-900 border-slate-900 text-white" data-testid="role-card-employer">
              <div className="flex items-center justify-between mb-6">
                <Badge className="bg-white/10 text-white hover:bg-white/10 font-mono text-[10px] tracking-widest uppercase">{t("landing.employers_label")}</Badge>
                <Briefcase className="w-6 h-6 text-white/40" />
              </div>
              <h3 className="font-display text-2xl font-semibold text-white mb-2">{t("landing.employers_title")}</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-6">{t("landing.employers_desc")}</p>
              <ul className="space-y-3 mb-6">
                {[t("landing.employers_b1"), t("landing.employers_b2"), t("landing.employers_b3"), t("landing.employers_b4")].map((txt, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/90">
                    <FileCheck className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    {txt}
                  </li>
                ))}
              </ul>
              <Link to="/register?role=employer">
                <Button className="w-full bg-white text-slate-900 hover:bg-slate-100" data-testid="cta-employer">
                  {t("landing.employers_cta")}
                  <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="mec-overline mb-3">{t("landing.features_overline")}</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 max-w-2xl mb-12">
            {t("landing.features_title")}
          </h2>
          <div className="grid md:grid-cols-12 gap-4">
            <div className="md:col-span-7 mec-card p-8 mec-card-hover relative overflow-hidden">
              <div className="relative z-10 max-w-md">
                <Wand2 className="w-7 h-7 text-[#004EEB] mb-4" />
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">{t("landing.feat_ai_title")}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{t("landing.feat_ai_desc")}</p>
              </div>
              <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden md:block">
                <img src={REMOTE_IMG} alt="" className="w-full h-full object-cover opacity-50" />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-white/40 to-white" />
              </div>
            </div>
            <div className="md:col-span-5 mec-card p-8 mec-card-hover">
              <Search className="w-7 h-7 text-[#004EEB] mb-4" />
              <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">{t("landing.feat_search_title")}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{t("landing.feat_search_desc")}</p>
            </div>
            <div className="md:col-span-4 mec-card p-8 mec-card-hover">
              <MessageSquare className="w-7 h-7 text-[#004EEB] mb-4" />
              <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">{t("landing.feat_msg_title")}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{t("landing.feat_msg_desc")}</p>
            </div>
            <div className="md:col-span-4 mec-card p-8 mec-card-hover">
              <FileCheck className="w-7 h-7 text-[#004EEB] mb-4" />
              <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">{t("landing.feat_cv_title")}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{t("landing.feat_cv_desc")}</p>
            </div>
            <div className="md:col-span-4 mec-card p-8 mec-card-hover bg-slate-50">
              <Globe2 className="w-7 h-7 text-[#004EEB] mb-4" />
              <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">{t("landing.feat_world_title")}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{t("landing.feat_world_desc")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="mec-overline mb-3 text-blue-400">{t("landing.cta_overline")}</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mb-4">
              {t("landing.cta_title_1")} <br />
              <span className="text-blue-400">{t("landing.cta_title_2")}</span>
            </h2>
            <p className="text-white/70 mb-8 max-w-md">{t("landing.cta_sub")}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="bg-[#004EEB] hover:bg-[#003BCC] text-white h-12" data-testid="cta-bottom-register">
                  {t("landing.cta_register")}
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="h-12 bg-transparent border-white/30 text-white hover:bg-white hover:text-slate-900" data-testid="cta-bottom-pricing">
                  {t("landing.cta_pricing")}
                </Button>
              </Link>
            </div>
          </div>
          <div className="hidden md:block">
            <img src={TEAM_IMG} alt="" className="rounded-lg w-full h-72 object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
